import { Menu, MenuItem, Stack, Typography } from '@mui/material';
import {
  ArrowLeftRight,
  Clock,
  Handshake,
  LogOut,
  type LucideIcon,
  Monitor,
  Moon,
  Sun,
} from 'lucide-react';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ColoredAvatar, ShortcutText, Tooltip } from 'src/components';
import { SelectPartnerDialog } from 'src/components/Navbar/select-partner-dialog';
import {
  PartnerOption,
  usePartnersDialogOptions,
} from 'src/components/Navbar/use-partners-dialog-options';
import { useThemeSwitch } from 'src/components/Navbar/use-theme-switch';
import { useTimeFormatSwitch } from 'src/components/Navbar/use-time-format-switch';
import { appShortcuts } from 'src/constants/appShortcuts';
import { useGroupRules, UserGroupRules, useSelectedPartner } from 'src/graphql';
import { useTimeFormat } from 'src/graphql/entities/time-format/use-time-format';
import { buildPartnerUrl, getPartnerFriendlyIdentifier } from 'src/routes/hooks';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods } from 'src/store/modals';
import { getDropdownMenuPaperSx } from 'src/utils/dropdown-paper';
import { useLogout, useRegisterShortcuts } from 'src/utils/hooks';
import { useScreenDimensions } from 'src/utils/system';
import { ItemDivider, SupportLinks } from './SupportLinks/support-links';

const REFER_PARTNER_LINK = 'https://www.onevisionresources.com/refer-a-partner';

const THEME_ICONS: Record<string, LucideIcon> = {
  light_mode: Sun,
  dark_mode: Moon,
  contrast: Monitor,
};

export const AppMenu = ({
  anchor,
  onClose,
}: {
  anchor: HTMLElement | null;
  onClose: () => void;
}) => {
  const {
    selectedTheme,
    setForceThemeMode,
    themeModalAnchor,
    openThemeSwitch,
    closeThemeSwitch,
    themeOptions,
  } = useThemeSwitch();

  const { isDesktop } = useScreenDimensions();
  const { logout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const isMultiTenancyMode = useGlobalStore.use.isMultiTenancyMode();
  const { checkValidity } = useGroupRules();

  const { selectedPartner } = useSelectedPartner();
  const { showModal: showCreatePartnerModal } = useModalMethods(Modals.CreatePartnerModal);
  const { partnersOptions: allPartnersOptions } = usePartnersDialogOptions({
    currentPartner: false,
  });

  const [partnerDialogOpened, setPartnerDialogOpened] = useState(false);

  const [clockFormatSwitchAnchor, setClockFormatSwitchAnchor] = useState<null | HTMLElement>(
    null,
  );

  const { currentTimeFormat, changeTimeFormat } = useTimeFormat();

  const canSwitchPartner = useMemo(() => {
    return allPartnersOptions.some(
      (option) => option.id !== null && option.id !== selectedPartner?.partnerId,
    );
  }, [allPartnersOptions, selectedPartner?.partnerId]);

  const canOpenPartnerDialog = isMultiTenancyMode && canSwitchPartner;

  useRegisterShortcuts(
    useMemo(
      () =>
        canOpenPartnerDialog
          ? [
              {
                ...appShortcuts.openSelectPartnerDialog,
                callback: () => {
                  setPartnerDialogOpened(true);
                  onClose();
                  return false;
                },
              },
            ]
          : [],
      [canOpenPartnerDialog, onClose],
    ),
  );
  const { selectedTimeFormatLabel, timeFormatsMenu } = useTimeFormatSwitch({
    isOpen: Boolean(clockFormatSwitchAnchor),
    anchorEl: clockFormatSwitchAnchor,
    selectedFormat: currentTimeFormat,
    onClose: () => setClockFormatSwitchAnchor(null),
    onChange: (value) => {
      changeTimeFormat(value);
      onClose();
    },
  });

  const onPartnerDialogChange = useCallback(
    (option: PartnerOption) => {
      if (option.id === (selectedPartner?.id ?? null)) {
        return;
      }

      setPartnerDialogOpened(false);

      // defer navigation to let it close modal first
      setTimeout(() => {
        navigate(
          buildPartnerUrl({
            existingIdFromUrl: getPartnerFriendlyIdentifier(selectedPartner),
            newPartner: option.rawPartner,
            pathname: location.pathname,
          }),
          { replace: true, flushSync: true },
        );
      });
    },
    [location.pathname, navigate, selectedPartner, setPartnerDialogOpened],
  );

  const switchPartnerMenuItem = selectedPartner ? (
    <Stack
      direction="row"
      sx={{
        gap: '1rem',
        width: '16rem',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <ColoredAvatar name={selectedPartner.businessName} />

      <Tooltip
        title={
          <ShortcutText
            label="Switch Partner"
            shortcut={appShortcuts.openSelectPartnerDialog.command}
            size="small"
          />
        }
      >
        <Typography flex={1} textAlign="left" overflow="hidden" textOverflow="ellipsis">
          {selectedPartner.businessName}
        </Typography>
      </Tooltip>
      <ArrowLeftRight size={20} />
    </Stack>
  ) : (
    <Stack
      direction="row"
      sx={{
        gap: '1rem',
        width: '8rem',
      }}
    >
      <ArrowLeftRight size={20} />

      <Tooltip
        title={
          <ShortcutText
            label="Switch Partner"
            shortcut={appShortcuts.openSelectPartnerDialog.command}
            size="small"
          />
        }
      >
        <Typography textAlign="center">Switch Partner</Typography>
      </Tooltip>
    </Stack>
  );

  const openClockFormatSwitch = (event: MouseEvent<HTMLElement>) => {
    setClockFormatSwitchAnchor((current) => (current ? null : event.currentTarget));
  };

  const openReferPartnerLink = useCallback(() => {
    window.open(REFER_PARTNER_LINK, '_blank');
    onClose();
  }, [onClose]);

  const ThemeIcon = THEME_ICONS[selectedTheme?.icon ?? ''] ?? Monitor;

  const canCreatePartner = checkValidity([UserGroupRules.AccessToCreateNewPartner]);

  const openCreatePartnerFlow = useCallback(() => {
    setPartnerDialogOpened(false);
    onClose();
    showCreatePartnerModal({
      onReturnToSelector: () => {
        setPartnerDialogOpened(true);
      },
    });
  }, [onClose, showCreatePartnerModal]);

  return (
    <>
      <Menu
        anchorEl={anchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isDesktop ? 'left' : 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: isDesktop ? 'left' : 'right',
        }}
        open={Boolean(anchor)}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: (theme) => getDropdownMenuPaperSx(theme),
          },
        }}
      >
        <MenuItem
          data-testid="app-menu-switch-partner"
          disabled={!canOpenPartnerDialog}
          onClick={() => {
            if (!canOpenPartnerDialog) return;
            setPartnerDialogOpened(true);
            onClose();
          }}
        >
          {switchPartnerMenuItem}
        </MenuItem>

        {selectedPartner?.currentPartner === 'yes' && (
          <SupportLinks anchor={anchor} onCloseMenu={onClose} />
        )}

        <MenuItem sx={{ gap: '1rem' }} onClick={openThemeSwitch}>
          <ThemeIcon size={20} />
          <Typography textAlign="center">{selectedTheme?.label} Theme</Typography>
        </MenuItem>

        <ItemDivider />

        <MenuItem sx={{ gap: '1rem' }} onClick={openClockFormatSwitch}>
          <Clock size={20} />
          <Typography textAlign="center">{selectedTimeFormatLabel}</Typography>
        </MenuItem>

        <ItemDivider />

        <MenuItem sx={{ gap: '1rem' }} onClick={openReferPartnerLink}>
          <Handshake size={20} />
          <Typography textAlign="center">Refer a Partner ($)</Typography>
        </MenuItem>

        <MenuItem sx={{ gap: '1rem' }} onClick={logout}>
          <LogOut size={20} />
          <Typography textAlign="center">Log out</Typography>
        </MenuItem>
      </Menu>

      {timeFormatsMenu}

      <Menu
        anchorEl={themeModalAnchor}
        open={Boolean(themeModalAnchor)}
        onClose={closeThemeSwitch}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              ...getDropdownMenuPaperSx(theme),
              minWidth: 200,
            }),
          },
        }}
      >
        {themeOptions.map((option) => {
          const OptionIcon = THEME_ICONS[option.icon] ?? Monitor;

          return (
            <MenuItem
              selected={option.value === selectedTheme?.value}
              key={option.label}
              onClick={() => {
                setForceThemeMode(option.value);
                closeThemeSwitch();
                onClose();
              }}
            >
              <OptionIcon size={20} />
              <Typography textAlign="center">{option.label}</Typography>
            </MenuItem>
          );
        })}
      </Menu>

      {canOpenPartnerDialog ? (
        <SelectPartnerDialog
          canCreatePartner={canCreatePartner}
          onClose={() => {
            setPartnerDialogOpened(false);
            onClose();
          }}
          onCreatePartner={openCreatePartnerFlow}
          open={partnerDialogOpened}
          onChange={onPartnerDialogChange}
        />
      ) : null}
    </>
  );
};
